import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Users, Send, Percent, CalendarClock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = { percent_off: "Desconto %", free_days: "Dias grátis" };

export const CouponsAdmin = ({ token }) => {
  const auth = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
  const [coupons, setCoupons] = useState([]);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent_off", value: "", description: "" });

  const load = useCallback(async () => {
    try {
      const [rc, rs] = await Promise.all([
        fetch(`${API}/api/backoffice/coupons`, { headers: auth }),
        fetch(`${API}/api/backoffice/promo-subscribers`, { headers: auth }),
      ]);
      if (rc.ok) setCoupons((await rc.json()).coupons || []);
      if (rs.ok) setSubs((await rs.json()).subscribers || []);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    const r = await fetch(`${API}/api/backoffice/coupons/${c.code}`, { method: "PUT", headers: auth, body: JSON.stringify({ active: !c.active }) });
    if (r.ok) { toast.success(!c.active ? "Cupom ativado" : "Cupom desativado", { description: c.code }); load(); }
    else toast.error("Falha ao atualizar cupom");
  };

  const remove = async (c) => {
    const r = await fetch(`${API}/api/backoffice/coupons/${c.code}`, { method: "DELETE", headers: auth });
    if (r.ok) { toast.success("Cupom removido", { description: c.code }); load(); }
    else toast.error("Falha ao remover");
  };

  const create = async (e) => {
    e.preventDefault();
    const code = form.code.trim().toUpperCase();
    if (!code) return toast.error("Informe o código do cupom.");
    const body = {
      code, type: form.type, description: form.description.trim(),
      percent_off: form.type === "percent_off" ? Number(form.value) || 0 : 0,
      free_days: form.type === "free_days" ? Number(form.value) || 0 : 0,
      active: true,
    };
    const r = await fetch(`${API}/api/backoffice/coupons`, { method: "POST", headers: auth, body: JSON.stringify(body) });
    const d = await r.json();
    if (!r.ok) return toast.error("Não foi possível criar", { description: d.detail });
    toast.success("Cupom criado", { description: code });
    setForm({ code: "", type: "percent_off", value: "", description: "" });
    load();
  };

  const notify = async (s) => {
    const r = await fetch(`${API}/api/backoffice/promo-subscribers/${s.id}/notify`, { method: "POST", headers: auth });
    const d = await r.json();
    if (!r.ok) return toast.error("Não foi possível notificar", { description: d.detail });
    toast.success("Aviso enviado", { description: `E-mail: ${d.email_sent ? "enviado" : "não enviado"}` });
    load();
  };

  return (
    <div className="space-y-6" data-testid="coupons-admin">
      {/* Criar cupom */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-600"><Plus className="h-4 w-4" /></div>
          <h2 className="font-heading text-base font-bold text-slate-900">Novo cupom</h2>
        </div>
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div>
            <label className="text-xs font-semibold text-slate-600">Código</label>
            <input data-testid="coupon-new-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="VELOXX" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Tipo</label>
            <select data-testid="coupon-new-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="percent_off">Desconto %</option>
              <option value="free_days">Dias grátis</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">{form.type === "percent_off" ? "% desconto" : "Dias grátis"}</label>
            <input data-testid="coupon-new-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === "percent_off" ? "50" : "30"} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Descrição</label>
            <input data-testid="coupon-new-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: 50% na 1ª mensalidade" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <button data-testid="coupon-create-btn" type="submit" className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800">
            <Plus className="h-4 w-4" /> Criar
          </button>
        </form>
      </div>

      {/* Lista de cupons */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
          <Ticket className="h-4 w-4 text-violet-600" />
          <h2 className="font-heading text-base font-bold text-slate-900">Cupons ({coupons.length})</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {coupons.length === 0 && <p className="py-10 text-center text-sm text-slate-400">Nenhum cupom cadastrado.</p>}
          {coupons.map((c) => (
            <div key={c.code} data-testid={`coupon-row-${c.code}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${c.type === "percent_off" ? "bg-violet-50 text-violet-600" : "bg-emerald-50 text-emerald-600"}`}>
                  {c.type === "percent_off" ? <Percent className="h-4 w-4" /> : <CalendarClock className="h-4 w-4" />}
                </div>
                <div>
                  <p className="font-mono text-sm font-bold text-slate-900">{c.code}</p>
                  <p className="text-xs text-slate-500">{c.description || TYPE_LABEL[c.type]} · {c.type === "percent_off" ? `${c.percent_off}%` : `${c.free_days} dias`} · <span className="font-semibold">{c.uses}</span> uso(s)</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${c.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${c.active ? "bg-emerald-500" : "bg-slate-400"}`} />{c.active ? "Ativo" : "Inativo"}
                </button>
                <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Assinantes usando cupom */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
          <Users className="h-4 w-4 text-violet-600" />
          <h2 className="font-heading text-base font-bold text-slate-900">Assinantes em promoção ({subs.length})</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {subs.length === 0 && <p className="py-10 text-center text-sm text-slate-400">Nenhum assinante usando cupom no momento.</p>}
          {subs.map((s) => (
            <div key={s.id} data-testid={`promo-sub-row-${s.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">{s.name || s.email}</p>
                <p className="text-xs text-slate-500">{s.email} · cupom <span className="font-mono font-bold">{s.coupon}</span> · {s.blocked ? <span className="font-semibold text-rose-600">bloqueado (promoção vencida)</span> : <span className="text-emerald-600">{s.days_left} dia(s) restantes</span>}</p>
              </div>
              <div className="flex items-center gap-2">
                {s.promo_notified && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Avisado</span>}
                <button data-testid={`promo-notify-${s.id}`} onClick={() => notify(s)} className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
                  <Send className="h-3.5 w-3.5" /> {s.promo_notified ? "Reenviar aviso" : "Enviar aviso"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
